import { fmtFecha } from '../../lib/utils';
import { useState, useEffect } from 'react';
import { comprasApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { RefreshCw, AlertTriangle, Clock, CheckCircle, Package, Printer } from 'lucide-react';
import { OCNumber } from '../../lib/ocFormat';

export const ReportePendientes = () => {
  const [loading, setLoading] = useState(true);
  const [ocPendientes, setOcPendientes] = useState(null);
  const [pedidosSinGestionar, setPedidosSinGestionar] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('oc');
  const [filtroOrigen, setFiltroOrigen] = useState('web'); // 'web' | 'access' | 'todas'

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroOrigen]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [ocRes, pedidosRes] = await Promise.all([
        comprasApi.getOCPendientes(filtroOrigen),
        comprasApi.getPedidosSinGestionar()
      ]);
      setOcPendientes(ocRes.data);
      setPedidosSinGestionar(pedidosRes.data);
    } catch (err) {
      setError('Error al cargar los datos');

    } finally {
      setLoading(false);
    }
  };

  const fmt = (num) => num?.toLocaleString('es-CL') || '0';
  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">{error}</p>
        <Button onClick={loadData} className="mt-4">Reintentar</Button>
      </div>
    );
  }

  return (
    <div className="p-4" data-testid="reporte-pendientes-page">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 print:hidden">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            Pendientes de Gestión
          </h1>
          <p className="text-sm text-muted-foreground">
            OC sin aprobar, OC sin recepción, Pedidos sin OC
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {/* Toggle Origen Web/Access/Todas */}
          <div className="flex items-center gap-1 text-xs border rounded-lg overflow-hidden" data-testid="reporte-pendientes-origen-toggle">
            <span className="px-2 py-1.5 text-gray-500 bg-gray-50">Origen:</span>
            <button
              onClick={() => setFiltroOrigen('web')}
              data-testid="reporte-pendientes-origen-web"
              className={`px-3 py-1.5 transition-colors ${filtroOrigen === 'web' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              title="Solo OCs creadas desde la web (accionables)"
            >
              Web
            </button>
            <button
              onClick={() => setFiltroOrigen('access')}
              data-testid="reporte-pendientes-origen-access"
              className={`px-3 py-1.5 transition-colors ${filtroOrigen === 'access' ? 'bg-slate-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              title="OCs legacy migradas desde Microsoft Access"
            >
              Access
            </button>
            <button
              onClick={() => setFiltroOrigen('todas')}
              data-testid="reporte-pendientes-origen-todas"
              className={`px-3 py-1.5 transition-colors ${filtroOrigen === 'todas' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              Todas
            </button>
          </div>
          <Button onClick={loadData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={handlePrint} variant="outline" size="sm">
            <Printer className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-500" />
            <div>
              <p className="text-sm text-orange-700">OC Sin Aprobar</p>
              <p className="text-xl font-bold text-orange-700">{ocPendientes?.cantidad_sin_aprobar || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-500" />
            <div>
              <p className="text-sm text-blue-700">OC Sin Recepción</p>
              <p className="text-xl font-bold text-blue-700">{ocPendientes?.cantidad_sin_recepcion || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <div>
              <p className="text-sm text-red-700">Pedidos Sin OC</p>
              <p className="text-xl font-bold text-red-700">{pedidosSinGestionar?.total || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b print:hidden">
        <button
          className={`px-4 py-2 text-sm font-medium ${activeTab === 'oc' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'}`}
          onClick={() => setActiveTab('oc')}
        >
          OC Pendientes ({(ocPendientes?.cantidad_sin_aprobar || 0) + (ocPendientes?.cantidad_sin_recepcion || 0)})
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium ${activeTab === 'pedidos' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'}`}
          onClick={() => setActiveTab('pedidos')}
        >
          Pedidos Sin Gestionar ({pedidosSinGestionar?.total || 0})
        </button>
      </div>

      {/* Tab Content - OC */}
      {activeTab === 'oc' && (
        <div className="space-y-6">
          {/* OC Sin Aprobar */}
          <div>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4 text-orange-500" />
              OC Sin Aprobar ({ocPendientes?.cantidad_sin_aprobar || 0})
            </h3>
            {ocPendientes?.oc_sin_aprobar?.length > 0 ? (
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-orange-100">
                    <th className="border p-1 text-left">N° OC</th>
                    <th className="border p-1 text-left">Proveedor</th>
                    <th className="border p-1 text-center">OT</th>
                    <th className="border p-1 text-center">Fecha</th>
                    <th className="border p-1 text-right">Neto</th>
                  </tr>
                </thead>
                <tbody>
                  {ocPendientes.oc_sin_aprobar.slice(0, 20).map((oc, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="border p-1 font-mono">{oc.numero_oc}</td>
                      <td className="border p-1">{oc.proveedor}</td>
                      <td className="border p-1 text-center">{oc.ot_numero || '-'}</td>
                      <td className="border p-1 text-center">{fmtFecha(oc.fecha)}</td>
                      <td className="border p-1 text-right font-mono">${fmt(oc.neto)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" /> Todas las OC están aprobadas
              </p>
            )}
          </div>

          {/* OC Sin Recepción */}
          <div>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <Package className="w-4 h-4 text-blue-500" />
              OC Aprobadas Sin Recepción ({ocPendientes?.cantidad_sin_recepcion || 0})
            </h3>
            {ocPendientes?.oc_sin_recepcion?.length > 0 ? (
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-blue-100">
                    <th className="border p-1 text-left">N° OC</th>
                    <th className="border p-1 text-left">Proveedor</th>
                    <th className="border p-1 text-center">OT</th>
                    <th className="border p-1 text-center">Fecha</th>
                    <th className="border p-1 text-right">Neto</th>
                  </tr>
                </thead>
                <tbody>
                  {ocPendientes.oc_sin_recepcion.slice(0, 20).map((oc, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="border p-1 font-mono"><OCNumber oc={oc} size="sm" /></td>
                      <td className="border p-1">{oc.proveedor}</td>
                      <td className="border p-1 text-center">{oc.ot_numero || '-'}</td>
                      <td className="border p-1 text-center">{fmtFecha(oc.fecha)}</td>
                      <td className="border p-1 text-right font-mono">${fmt(oc.neto)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" /> Todas las OC aprobadas tienen recepción
              </p>
            )}
          </div>
        </div>
      )}

      {/* Tab Content - Pedidos */}
      {activeTab === 'pedidos' && (
        <div>
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            Pedidos con Items Sin OC ({pedidosSinGestionar?.total || 0})
          </h3>
          {pedidosSinGestionar?.pedidos_sin_gestionar?.length > 0 ? (
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-red-100">
                  <th className="border p-1 text-left">N° Pedido</th>
                  <th className="border p-1 text-center">Fecha</th>
                  <th className="border p-1 text-left">Solicitante</th>
                  <th className="border p-1 text-center">OTs Afectadas</th>
                  <th className="border p-1 text-center">Items Sin OC</th>
                </tr>
              </thead>
              <tbody>
                {pedidosSinGestionar.pedidos_sin_gestionar.map((p, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border p-1 font-mono">{p.numero_pedido}</td>
                    <td className="border p-1 text-center">{fmtFecha(p.fecha)}</td>
                    <td className="border p-1">{p.solicitante}</td>
                    <td className="border p-1 text-center font-mono">{p.ots_afectadas?.join(', ') || '-'}</td>
                    <td className="border p-1 text-center font-bold text-red-600">{p.items_sin_oc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-green-600 flex items-center gap-1">
              <CheckCircle className="w-4 h-4" /> Todos los pedidos tienen OC generada
            </p>
          )}
        </div>
      )}
    </div>
  );
};
